import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  orderBy as firestoreOrderBy,
} from "firebase/firestore";
import moment from "moment";
import { FirestoreDB } from "./firebase";
import { mean, map, countBy, orderBy } from "lodash";
const { DateTime } = require("luxon");

export const getXAxis = (duration) => {
  if (duration === 7) {
    return new Array(7)
      .fill()
      .map((_, i) => moment().subtract(i + 1, "days"))
      .filter((date) => date.day() !== 0 && date.day() !== 6)
      .reverse();
  }
  if (duration === 30) {
    return Array(4)
      .fill()
      .map((_, index) => `week ${index + 1}`);
  }
  if (duration === 90) {
    return Array(13)
      .fill()
      .map((_, index) => `week ${index + 1}`);
  }
};
export const getRatingPoints = (type, duration, data) => {
  if (!data) return;
  // for one week
  if (duration === 7) {
    return getXAxis(duration).map(
      (date, i) =>
        parseFloat(
          data[`${type}`]["7days"][`${moment(date).format("YYYYMMDD")}`]
            ?.averageRatings
        ).toFixed(1) || null
    );
  }
  // for one month duration
  if (duration === 30) {
    const sortedData = {};
    for (let i = 1; i <= Object.keys(data[`${type}`]["28days"]).length; i++) {
      sortedData[`week ${i}`] = data[`${type}`]["28days"][`week_${i}`];
    }
    return Object.keys(sortedData)
      .map((key) => parseFloat(sortedData[key].averageRatings).toFixed(1))
      .reverse();
  }

  // for 3 months duration
  if (duration === 90) {
    const sortedData = {};
    for (let i = 1; i <= Object.keys(data[`${type}`]["91days"]).length; i++) {
      sortedData[`week ${i}`] = data[`${type}`]["91days"][`week_${i}`];
    }
    return Object.keys(sortedData)
      .map((key) => parseFloat(sortedData[key].averageRatings).toFixed(1))
      .reverse();
  }
};

export const getComparisonColor = (value) => {
  return value?.substr(value?.length - 4) === "more" ? "red" : "#58E490";
};

export const getApiDuration = (duration) => {
  return duration === 7 ? "7 days" : duration === 30 ? "28 days" : "91 days";
};

export const getRatingPointsForIndividual = (type, duration, data) => {
  if (!data) return;
  // for one week
  if (duration === 7) {
    const week = data?.[`${type}`]?.["7 days"] || {};
    return getXAxis(duration).map(
      (date, i) =>
        parseFloat(
          week[`${moment(date).format("YYYYMMDD")}`]?.averageRatings
        ).toFixed(1) || null
    );
  }
  // for one month duration
  if (duration === 30) {
    const weeks = data?.[`${type}`]?.["28 days"] || {};
    const sortedData = {};
    for (let i = 1; i <= Object.keys(weeks).length; i++) {
      sortedData[`week ${i}`] = weeks[`week_${i}`];
    }
    return Object.keys(sortedData)
      .map((key) => parseFloat(sortedData[key]?.averageRatings).toFixed(1))
      .reverse();
  }

  // for 3 months duration
  if (duration === 90) {
    const weeks = data?.[`${type}`]?.["91 days"] || {};
    const sortedData = {};
    for (let i = 1; i <= Object.keys(weeks).length; i++) {
      sortedData[`week ${i}`] = weeks[`week_${i}`];
    }
    return Object.keys(sortedData)
      .map((key) => parseFloat(sortedData[key]?.averageRatings).toFixed(1))
      .reverse();
  }
};

export const getDrComparisonStyles = (value) => {
  if (value >= 0) {
    return "#58E490";
  } else return "red";
};

export const getXAxisForEffDis = (data) => {
  if (!data) return;
  return Object.keys(data);
};

export const getPointsForMeetingDis = (data, type) => {
  return Object.keys(data).map((key) => data[key][type].totalMeetings);
};

export const getPointsForEffDis = (data, type) => {
  let ratings = [];
  Object.keys(data).map((key) => ratings.push(data[key][type]?.ratings));
  const avgRatings = ratings.map((list) => {
    const intList = list.map((value) => parseInt(value));
    return isNaN(mean(intList)) ? 0 : parseFloat(mean(intList)).toFixed(1);
  });
  return avgRatings;
};

// fetching table data
export const fetchOrgMeeting = async (orgId, pastDays = 7) => {
  let today = DateTime.local().startOf("day");
  const pastDate = new Date(
    today.toLocal().minus({ days: pastDays }).startOf("day").toMillis()
  );
  today = new Date(today.minus({ second: 1 }).toMillis());
  try {
    // get all the users who are the part of organization
    const currentOrgUsers = [];
    const usersQuery = query(
      collection(FirestoreDB, `users`),
      where("subscription.organization_id", "==", orgId)
    );
    const querySnapshot = await getDocs(usersQuery);
    for (let i = 0; i < querySnapshot.docs.length; i++) {
      currentOrgUsers.push({
        ...querySnapshot.docs[i].data(),
        credentials: null,
        googleSync: null,
        lastLoginAt: null,
        lastSignInTime: null,
        channel: null,
      });
    }
    // get all the meetings from currentOrgUsers where they are the organizer
    let meetingsWhereCurrentOrgUsersAsOrgnaizer = [];
    for (let i = 0; i < currentOrgUsers.length; i++) {
      const currentUserMeetings = [];
      const q = query(
        collection(
          FirestoreDB,
          `users/${currentOrgUsers[i].uid}/calendarEvents`
        ),
        where("formattedStartTime", ">=", pastDate),
        where("formattedStartTime", "<=", today),
        where("organizer.self", "==", true)
      );
      const snapShot = await getDocs(q);

      for (let j = 0; j < snapShot.docs.length; j++) {
        currentUserMeetings.push(snapShot.docs[j].data());
      }
      meetingsWhereCurrentOrgUsersAsOrgnaizer.push(
        ...currentUserMeetings.filter((meeting) => {
          return (
            meeting?.attendees &&
            meeting?.attendees?.filter((attendee) => {
              return (
                attendee?.email.split("@")[1] !==
                  "resource.calendar.google.com" &&
                attendee?.responseStatus?.toLowerCase() !== "declined"
              );
            })?.length > 1
          );
        })
      );
      // console.log(meetingsWhereCurrentOrgUsersAsOrgnaizer)
    }

    const meetingsWithMetaData = [];

    for (let i = 0; i < meetingsWhereCurrentOrgUsersAsOrgnaizer.length; i++) {
      const meetingId = meetingsWhereCurrentOrgUsersAsOrgnaizer[i].id;
      const meetingRef = doc(FirestoreDB, "meetings", meetingId);
      const usersQuery = query(collection(meetingRef, "users"));

      const meetingSnapshot = await getDoc(meetingRef);
      const meetingData = meetingSnapshot.data();

      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map((doc) => doc.data());

      const mergedData = {
        ...meetingData,
        users: usersData,
      };

      meetingsWithMetaData.push(mergedData);
    }

    const mergedData = mergeMeetingsAndMetaData(
      meetingsWhereCurrentOrgUsersAsOrgnaizer,
      meetingsWithMetaData,
      orgId
    );
    // console.log("merged: ", meetingsWithMetaData);
    return mergedData;
    // return meetingsWhereCurrentOrgUsersAsOrgnaizer;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const mergeMeetingsAndMetaData = (meetings, metaData, orgId) => {
  // console.log(meetings, metaData, orgId);
  const merged = meetings.map((meeting, i) => {
    return {
      ...meetings[i],
      cost: parseFloat(metaData[i]?.costInfo?.[`${orgId}`]?.amount).toFixed(1),
      averageRating: getAverateRatingOfMeeting(metaData[i]?.users),
    };
  });
  //would complete if i need
  return merged;
};

const getAverateRatingOfMeeting = (users) => {
  if (users.length > 0) {
    const ratings = users.map((user) =>
      user?.rating ? parseFloat(user?.rating) : 0
    );
    return parseFloat(mean(ratings)).toFixed(1);
  } else return 0;
};

export const getHighestFeedback = (feedbacks) => {
  if (feedbacks && feedbacks.length > 0) {
    if (feedbacks?.length > 0) {
      const groupedArray = orderBy(
        map(countBy(feedbacks), (value, key) => ({
          [key]: value,
        })),
        (value) => Object.values(value)[0]
      );
      return groupedArray.reverse();
    }
  } else return [];
};

export const getOrgMeetingDist = (userEmail, data) => {
  if (data) {
    // format of output
    let output = {
      byLength: {
        lessThan15: {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
        },
        "15-30": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
        },
        "31-60": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
        },
        "61-120": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
        },
        moreThan120: {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            aboveAverageRating: 0,
            averageRating: 0,
            belowAverageRating: 0,
            ratings: [],
          },
        },
      },
      bySize: {
        1: {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
        },
        "2-5": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
        },
        "6-10": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
        },
        "11-20": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
        },
        "20+": {
          internal: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
          external: {
            totalMeetings: 0,
            totalDuration: 0,
            ratings: [],
          },
        },
      },
      feedbacks: [],
    };
    const userEmailSuffix = userEmail?.split("@")[1];

    data.map((meeting) => {
      const startTime = DateTime.fromISO(meeting?.start?.dateTime).toMillis();
      const attendees = meeting?.attendees;
      if (startTime && attendees) {
        const endTime = DateTime.fromISO(meeting?.end?.dateTime).toMillis();
        const duration = (endTime - startTime) / 1000 / 60;
        const duration_category =
          duration <= 15
            ? "lessThan15"
            : duration <= 30
            ? "15-30"
            : duration <= 60
            ? "31-60"
            : duration <= 120
            ? "61-120"
            : "moreThan120";

        const attendeesLength = attendees?.length;
        const attendees_category =
          attendeesLength == 1
            ? "1"
            : attendeesLength <= 5
            ? "2-5"
            : attendeesLength <= 10
            ? "6-10"
            : attendeesLength <= 20
            ? "11-20"
            : "20+";

        let isInternalMeeting = true;
        for (let i = 0; i < attendees?.length; i++) {
          if (
            attendees[i]?.email?.split("@")[1] !== userEmailSuffix &&
            attendees[i]?.responseStatus?.toLowerCase() !== "declined"
          ) {
            isInternalMeeting = false;
            break;
          }
        }

        const meetingType = isInternalMeeting ? "internal" : "external";
        output.byLength[duration_category][meetingType].totalMeetings += 1;
        output.byLength[duration_category][meetingType].totalDuration +=
          duration;

        output.bySize[attendees_category][meetingType].totalMeetings += 1;

        if (meeting?.ratings?.length > 0) {
          meeting?.ratings.map((rating) => {
            const rating_range =
              rating > 4
                ? "aboveAverageRating"
                : rating > 3
                ? "averageRating"
                : "belowAverageRating";
            output.byLength[duration_category][meetingType].ratings.push(
              rating
            );
            output.byLength[duration_category][meetingType][rating_range] +=
              rating;

            output.bySize[attendees_category][meetingType].ratings.push(rating);
          });
        }
      }
    });
    return output;
  }
};

export const getStrategicGoal = (strategicGoals, eventId) => {
  let foundedGoal = null;
  if (strategicGoals) {
    Object.keys(strategicGoals).some((goal) => {
      return strategicGoals[goal]?.events?.some((id) => {
        if (id === eventId) {
          foundedGoal = goal;
          return true;
        }
        return false;
      });
    });
  }
  return foundedGoal || "no goal selected";
};

export const filterAttendees = (attendees) => {
  return attendees?.filter((attendee) => {
    return (
      attendee?.email?.split("@")[1] !== "resource.calendar.google.com" &&
      attendee?.responseStatus?.toLowerCase() !== "declined"
    );
  });
};

export const splitGoals = (strategicGoals, data) => {
  if (data) {
    let split = Object.keys(strategicGoals).map((goal) => ({
      name: goal,
      count: 0,
      meetingHours: 0,
      peopleHours: 0,
    }));
    data?.map((event) => {
      const goal = getStrategicGoal(
        strategicGoals,
        event?.recurringEventId || event?.id
      );
      if (goal) {
        const goalItem = split.find((item) => item.name === goal);
        if (goalItem) {
          // count of meetings for each goal
          goalItem.count++;

          // meetings hours for each goal
          const durationInHours = moment
            .duration(
              moment(event?.end?.dateTime).diff(moment(event?.start?.dateTime))
            )
            .asHours();
          goalItem.meetingHours = (
            parseFloat(goalItem.meetingHours) + durationInHours
          ).toFixed(1);
          goalItem.meetingHours = parseFloat(goalItem.meetingHours); // Parse as float

          //men hours for each goal
          const noOfAttendees = filterAttendees(event?.attendees).length || 0;
          goalItem.peopleHours = parseFloat(
            noOfAttendees * goalItem.meetingHours
          ).toFixed(1);
        }
      }
    });
    return split;
  } else {
    return [];
  }
};

export const fetchMeVsMeetingForMyOrg = async (user, duration) => {
  //! hardcoding pranavi's user id for now
  const q = query(
    doc(
      FirestoreDB,
      `users/LPSCRk7tvQO6HaWf2mME23MnKMD3/orgCalculations/meVsMeetingTime`
    )
  );
  const snapShot = await getDoc(q);
  const res = snapShot.data();

  const durations = {
    7: "7days",
    30: "28days",
    90: "91days",
  };
  const selectedDuration = durations[`${duration}`];
  console.log(res?.[`${selectedDuration}`]?.ExcludingSelf);
  return res?.[`${selectedDuration}`]?.ExcludingSelf;
};

export const getMyOrgInsights = async (id) => {
  try {
    const snapShot = await getDocs(
      query(
        collection(FirestoreDB, `users/${id}/orgCalculations`),
        firestoreOrderBy("updatedAt", "desc"),
        limit(1)
      )
      //! FOR DEVELOPMENT
      // rahul's user id
      // query(
      //   collection(
      //     FirestoreDB,
      //     `users/hCxJ25vFN8XBgbAEVWXz0lM3IRP2/orgCalculations`
      //   ),
      //   firestoreOrderBy('updatedAt', 'desc'),
      //   limit(1)
      // )
    );
    // Since we're limiting to 1 document, we can return the first (and only) document
    if (!snapShot.empty) {
      return { ...snapShot.docs[0].data(), snapshotDate: snapShot.docs[0].id };
    }

    return null; // Return null if no documents found
  } catch (error) {
    console.error("Error while my org insights fetch: ", error);
    throw error;
  }
};

export const formatDuration = (duration) => {
  const durations = {
    7: "7days",
    30: "28days",
    90: "91days",
  };

  return durations[duration];
};

export const reverseFormatDuration = (duration) => {
  const durations = {
    "7days": 7,
    "28days": 30,
    "91days": 90,
  };
  return durations[duration];
};

export const getPrev7Days = () => {
  const currentDayOfWeek = moment().day();

  // Define an array of day names
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  // Initialize an empty array to store the previous seven day names
  const previousSevenDays = [];

  // Loop to get the previous seven days
  for (let i = 1; i <= 7; i++) {
    // Calculate the index of the previous day
    const previousDayIndex = (currentDayOfWeek - i + 7) % 7;

    // Add the name of the previous day to the array
    previousSevenDays.push(dayNames[previousDayIndex]);
  }

  return previousSevenDays.reverse();
};

export const formatOrgName = (orgName) => {
  const nameArray = orgName.split(".").slice(0, -1);
  return (
    nameArray[0].charAt(0).toUpperCase() +
    nameArray[0].slice(1) +
    " " +
    nameArray
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(".")
  );
};

export const getEffectivenessRatingMessage = (totalRatings, totalAttendees) => {
  if (!totalRatings || !totalAttendees || totalAttendees === 0) {
    return null;
  }

  const ratingPercentage = (totalRatings / totalAttendees) * 100;

  if (ratingPercentage < 30) {
    return {
      type: "critical",
      color: "red",
      message: `Very low response rate: Only ${totalRatings} ratings from ${totalAttendees} potential attendees (${Math.ceil(
        ratingPercentage
      )}%). The data quality is poor and may not represent actual meeting effectiveness.`,
    };
  } else if (ratingPercentage < 50) {
    return {
      type: "warning",
      color: "orange",
      message: `Total number of ratings (${totalRatings}) is less than 50% of total potential ratings (${totalAttendees}). The data might not give you the right quality of your meetings.`,
    };
  } else if (ratingPercentage < 70) {
    return {
      type: "moderate",
      color: "yellow",
      message: `Good response rate: ${totalRatings} ratings from ${totalAttendees} attendees (${Math.ceil(
        ratingPercentage
      )}%). Consider encouraging more feedback for better insights.`,
    };
  } else {
    return {
      type: "good",
      color: "green",
      message: `Excellent response rate: ${totalRatings} ratings from ${totalAttendees} attendees (${Math.ceil(
        ratingPercentage
      )}%). Your effectiveness data is highly reliable.`,
    };
  }
};

export const getEffectivenessRatingMessageForAttendee = (
  totalRatings,
  totalMeetings
) => {
  if (!totalRatings || !totalMeetings || totalMeetings === 0) {
    return null;
  }

  const ratingPercentage = (totalRatings / totalMeetings) * 100;

  if (ratingPercentage < 30) {
    return {
      type: "critical",
      color: "red",
      message: `You've rated very few meetings: Only ${totalRatings} out of ${totalMeetings} meetings you attended (${Math.ceil(
        ratingPercentage
      )}%). Your feedback helps improve meeting quality.`,
    };
  } else if (ratingPercentage < 50) {
    return {
      type: "warning",
      color: "orange",
      message: `You've rated ${totalRatings} out of ${totalMeetings} meetings (${Math.ceil(
        ratingPercentage
      )}%). Consider rating more meetings to track your meeting experience better.`,
    };
  } else if (ratingPercentage < 70) {
    return {
      type: "moderate",
      color: "yellow",
      message: `Good rating coverage: You've rated ${totalRatings} out of ${totalMeetings} meetings (${Math.ceil(
        ratingPercentage
      )}%). Keep up the feedback to improve meeting effectiveness.`,
    };
  } else {
    return {
      type: "good",
      color: "green",
      message: `Excellent rating coverage: You've rated ${totalRatings} out of ${totalMeetings} meetings (${Math.ceil(
        ratingPercentage
      )}%). Your consistent feedback helps create better meetings.`,
    };
  }
};

export const generateMeetingCostInsights = (inputData, type) => {
  let data = inputData;

  if (
    !data ||
    data.totalMeetingsCost === null ||
    data.totalMeetingsCost === undefined
  ) {
    return {
      message: "Loading meeting cost insights...",
      icon: "⏳",
    };
  }

  const {
    internalMeetingsCost,
    externalMeetingsCost,
    noOfExternalMeetings,
    noOfInternalMeetings,
    totalMeetingsCost,
  } = data;

  // Check if all required fields are present and valid numbers
  if (
    typeof internalMeetingsCost !== "number" ||
    typeof externalMeetingsCost !== "number" ||
    typeof noOfExternalMeetings !== "number" ||
    typeof noOfInternalMeetings !== "number" ||
    typeof totalMeetingsCost !== "number"
  ) {
    return {
      message: "Loading meeting cost insights...",
      icon: "⏳",
    };
  }

  const totalMeetings = noOfExternalMeetings + noOfInternalMeetings;

  // Handle edge cases
  if (totalMeetings === 0 && totalMeetingsCost > 0) {
    return {
      message: `You have meeting cost data ($${Math.ceil(
        totalMeetingsCost / 1000
      )}K total) but meeting count information is missing. Check your data source for complete meeting details.`,
      icon: "⚠️",
    };
  }

  if (totalMeetings === 0 && totalMeetingsCost === 0) {
    return {
      message: "No meeting activity detected for the selected period.",
      icon: "📭",
    };
  }

  // Calculate sophisticated metrics for correlations
  const internalMeetingPercentage = Math.ceil(
    (noOfInternalMeetings / totalMeetings) * 100
  );
  const externalMeetingPercentage = Math.ceil(
    (noOfExternalMeetings / totalMeetings) * 100
  );
  const internalCostPercentage = Math.ceil(
    (internalMeetingsCost / totalMeetingsCost) * 100
  );
  const externalCostPercentage = Math.ceil(
    (externalMeetingsCost / totalMeetingsCost) * 100
  );

  const avgInternalCost = Math.ceil(
    internalMeetingsCost / noOfInternalMeetings
  );
  const avgExternalCost = Math.ceil(
    externalMeetingsCost / noOfExternalMeetings
  );

  // Calculate cost efficiency and frequency ratios
  const costEfficiencyRatio = avgExternalCost / avgInternalCost;
  const frequencyDifference = Math.abs(
    internalMeetingPercentage - externalMeetingPercentage
  );
  const costDifference = Math.abs(
    internalCostPercentage - externalCostPercentage
  );

  // Generate sophisticated insights based on correlations

  // High cost efficiency disparity
  if (costEfficiencyRatio > 1.5) {
    return {
      message: `External meetings cost ${Math.ceil(
        (costEfficiencyRatio - 1) * 100
      )}% more per session than internal ones ($${avgExternalCost} vs $${avgInternalCost}). Despite being only ${externalMeetingPercentage}% of your meetings, they're driving disproportionate costs.`,
      icon: "📈",
    };
  }

  if (costEfficiencyRatio < 0.7) {
    return {
      message: `Internal meetings are surprisingly expensive at average cost of $${avgInternalCost} each, compared to average cost of $${avgExternalCost} for external meetings. Consider reviewing internal meeting duration and participant count for optimization opportunities.`,
      icon: "🔍",
    };
  }

  // Frequency vs cost imbalance analysis
  if (frequencyDifference > 20 && costDifference < 10) {
    const dominantType =
      internalMeetingPercentage > externalMeetingPercentage
        ? "internal"
        : "external";
    const dominantCount =
      dominantType === "internal"
        ? internalMeetingPercentage
        : externalMeetingPercentage;
    return {
      message: `Remarkable cost balance: ${dominantType} meetings dominate frequency (${dominantCount}% of meetings) but costs remain proportionally distributed. Your meeting cost structure is well-optimized across both types.`,
      icon: "⚖️",
    };
  }

  // High frequency with disproportionate cost
  if (internalMeetingPercentage > 65 && internalCostPercentage > 75) {
    return {
      message: `Internal meetings represent ${internalMeetingPercentage}% of your schedule but consume ${internalCostPercentage}% of meeting costs. Consider shorter durations or smaller participant groups to optimize internal collaboration costs.`,
      icon: "💼",
    };
  }

  // External meeting value analysis
  if (externalMeetingPercentage < 40 && externalCostPercentage > 45) {
    return {
      message: `External meetings, while only ${externalMeetingPercentage}% of your schedule, account for ${externalCostPercentage}% of costs. Ensure these strategic sessions deliver proportional value to justify the premium investment.`,
      icon: "🌐",
    };
  }

  // Cost parity insight
  if (Math.abs(costEfficiencyRatio - 1) < 0.2) {
    const avgCost = Math.ceil(totalMeetingsCost / totalMeetings);
    return {
      message: `Consistent meeting economics: Both internal (average cost of $${avgInternalCost}) and external (average cost of $${avgExternalCost}) meetings show similar per-session costs. Your ${totalMeetings} meetings average $${avgCost} each with balanced cost efficiency.`,
      icon: "📊",
    };
  }

  // Default balanced insight
  return {
    message: `Meeting portfolio analysis: ${internalMeetingPercentage}% internal meetings (${internalCostPercentage}% of costs) vs ${externalMeetingPercentage}% external meetings (${externalCostPercentage}% of costs). Average investment: $${Math.ceil(
      totalMeetingsCost / totalMeetings
    )} per meeting.`,
    icon: "📈",
  };
};

export const generateMeetingHoursInsights = (data, type, duration) => {
  if (!data) {
    return {
      message: "Loading meeting hours insights...",
      icon: "⏳",
    };
  }

  const {
    NoOfMeetings,
    NoOfHoursFromExternalMeetings,
    NoOfHoursFromInternalMeetings,
    NoOfExternalMeetings,
    NoOfInternalMeetings,
  } = data;

  // Check if all required fields are present and valid numbers
  if (
    typeof NoOfMeetings !== "number" ||
    typeof NoOfHoursFromExternalMeetings !== "number" ||
    typeof NoOfHoursFromInternalMeetings !== "number"
  ) {
    return {
      message: "Loading meeting hours insights...",
      icon: "⏳",
    };
  }

  const totalHours =
    NoOfHoursFromExternalMeetings + NoOfHoursFromInternalMeetings;
  const externalMeetingsCount = NoOfExternalMeetings || 0;
  const internalMeetingsCount = NoOfInternalMeetings || 0;
  const totalMeetingsCount = externalMeetingsCount + internalMeetingsCount;

  // Handle edge cases
  if (totalMeetingsCount === 0 && totalHours > 0) {
    return {
      message: `You have meeting hours data (${totalHours.toFixed(
        1
      )} total hours) but meeting count information is missing. Check your data source for complete meeting details.`,
      icon: "⚠️",
    };
  }

  if (totalMeetingsCount === 0 && totalHours === 0) {
    return {
      message: "No meeting activity detected for the selected period.",
      icon: "📭",
    };
  }

  // Calculate metrics
  const externalHoursPercentage = Math.ceil(
    (NoOfHoursFromExternalMeetings / totalHours) * 100
  );
  const internalHoursPercentage = Math.ceil(
    (NoOfHoursFromInternalMeetings / totalHours) * 100
  );
  const externalMeetingPercentage = Math.ceil(
    (externalMeetingsCount / totalMeetingsCount) * 100
  );
  const internalMeetingPercentage = Math.ceil(
    (internalMeetingsCount / totalMeetingsCount) * 100
  );

  const avgExternalMeetingHours =
    externalMeetingsCount > 0
      ? NoOfHoursFromExternalMeetings / externalMeetingsCount
      : 0;
  const avgInternalMeetingHours =
    internalMeetingsCount > 0
      ? NoOfHoursFromInternalMeetings / internalMeetingsCount
      : 0;
  const avgTotalMeetingHours =
    totalMeetingsCount > 0 ? totalHours / totalMeetingsCount : 0;

  // Calculate daily averages based on duration
  const daysInPeriod = duration === 7 ? 7 : duration === 30 ? 30 : 90;
  const avgDailyMeetings =
    Math.ceil((totalMeetingsCount / daysInPeriod) * 10) / 10;
  const avgDailyHours = Math.ceil((totalHours / daysInPeriod) * 10) / 10;

  // Generate insights based on patterns

  // High meeting load analysis
  if (avgDailyMeetings > 5) {
    return {
      message: `High meeting intensity: You're averaging ${avgDailyMeetings} meetings per day (${avgDailyHours} hours daily). Consider consolidating shorter meetings to create more focused work blocks.`,
      icon: "📅",
    };
  }

  if (avgDailyMeetings < 1) {
    return {
      message: `Low meeting activity: You're averaging ${avgDailyMeetings} meetings per day (${avgDailyHours} hours daily). This period shows minimal collaboration time.`,
      icon: "📉",
    };
  }

  // Meeting duration efficiency analysis
  if (avgTotalMeetingHours > 1.5) {
    return {
      message: `Long meeting sessions: Your meetings average ${avgTotalMeetingHours.toFixed(
        1
      )} hours each. Consider if shorter, focused sessions could achieve the same outcomes more efficiently.`,
      icon: "⏱️",
    };
  }

  if (avgTotalMeetingHours < 0.5) {
    return {
      message: `Quick meetings: Your meetings average ${avgTotalMeetingHours.toFixed(
        1
      )} hours each. Great for efficiency, but ensure complex topics get adequate discussion time.`,
      icon: "⚡",
    };
  }

  // External vs Internal balance analysis
  if (externalHoursPercentage > 70) {
    return {
      message: `External-focused schedule: ${externalHoursPercentage}% of your meeting time is external (${NoOfHoursFromExternalMeetings.toFixed(
        1
      )} hours). Great for networking and partnerships, but consider internal team alignment needs.`,
      icon: "🌐",
    };
  }

  if (internalHoursPercentage > 70) {
    return {
      message: `Internal collaboration focus: ${internalHoursPercentage}% of your meeting time is internal (${NoOfHoursFromInternalMeetings.toFixed(
        1
      )} hours). Strong team alignment, but ensure external relationships are maintained.`,
      icon: "👥",
    };
  }

  // Meeting efficiency insights
  if (Math.abs(externalHoursPercentage - externalMeetingPercentage) > 20) {
    const efficiencyType =
      externalHoursPercentage > externalMeetingPercentage
        ? "longer"
        : "shorter";
    return {
      message: `External meetings are ${efficiencyType} than average: ${externalHoursPercentage}% of hours vs ${externalMeetingPercentage}% of meetings. This suggests ${efficiencyType} external sessions with potentially higher strategic value.`,
      icon: "📊",
    };
  }

  // Balanced schedule insight
  if (Math.abs(externalHoursPercentage - internalHoursPercentage) < 15) {
    return {
      message: `Balanced meeting portfolio: ${internalHoursPercentage}% internal (${NoOfHoursFromInternalMeetings.toFixed(
        1
      )} hours) vs ${externalHoursPercentage}% external (${NoOfHoursFromExternalMeetings.toFixed(
        1
      )} hours). Good mix of team collaboration and external engagement.`,
      icon: "⚖️",
    };
  }

  // Default comprehensive insight
  return {
    message: `Meeting time analysis: ${totalMeetingsCount} meetings totaling ${totalHours.toFixed(
      1
    )} hours (${avgDailyMeetings} daily). ${internalHoursPercentage}% internal collaboration, ${externalHoursPercentage}% external engagement. Average session: ${avgTotalMeetingHours.toFixed(
      1
    )} hours.`,
    icon: "📈",
  };
};

export const filterValidMeetings = (meetings) => {
  const validMeetings = [];
  let skippedMeetings = 0;

  for (const meeting of meetings) {
    try {
      // Check if meeting has required time data
      if (!meeting.start?.dateTime || !meeting.end?.dateTime) {
        skippedMeetings++;
        console.warn(
          `⚠️  Skipping meeting without proper time data: ${meeting.id}`
        );
        continue;
      }

      // Skip all-day events
      if (meeting.start.date && !meeting.start.dateTime) {
        skippedMeetings++;
        console.warn(`⚠️  Skipping all-day event: ${meeting.id}`);
        continue;
      }

      const startTime = DateTime.fromISO(meeting.start.dateTime);
      const endTime = DateTime.fromISO(meeting.end.dateTime);

      // Validate dates
      if (!startTime.isValid || !endTime.isValid) {
        skippedMeetings++;
        console.warn(`⚠️  Skipping meeting with invalid dates: ${meeting.id}`);
        continue;
      }

      // Skip meetings with invalid duration
      if (endTime <= startTime) {
        skippedMeetings++;
        console.warn(
          `⚠️  Skipping meeting with invalid duration: ${meeting.id}`
        );
        continue;
      }

      // Apply attendee filters (minimum 2 attendees, exclude declined/resources)
      const attendees = meeting.attendees || [];
      const validAttendees = attendees.filter((attendee) => {
        return (
          attendee?.email?.split("@")[1] !== "resource.calendar.google.com" &&
          attendee?.responseStatus?.toLowerCase() !== "declined"
        );
      });

      // Only include meetings with at least 2 attendees
      if (validAttendees.length >= 2) {
        validMeetings.push(meeting);
      } else {
        skippedMeetings++;
        console.warn(
          `⚠️  Skipping meeting with insufficient attendees: ${meeting.id}`
        );
      }
    } catch (error) {
      skippedMeetings++;
      console.error(`❌ Error processing meeting ${meeting.id}:`, error);
    }
  }

  console.info(
    `📊 Filtered ${validMeetings.length} valid meetings, skipped ${skippedMeetings} meetings`
  );

  return validMeetings;
};

export const minutesBuckets = [
  "1-15",
  "16-30",
  "31-60",
  "61-120",
  "moreThan120",
];
export const attendeesBuckets = ["1:1", "3-5", "6-10", "11-20", "20+"];
export const weekdays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Read exact dates, since updatedAt can change when an older snapshot is rebuilt.
// The rules let a user read their own userCalculations, so their own pages skip
// the Functions. A manager viewing a report has to go through getInsights.
export const getMyUserInsights = async (uid) => {
  const snapshot = await getDocs(
    query(
      collection(FirestoreDB, `users/${uid}/userCalculations`),
      firestoreOrderBy("updatedAt", "desc"),
      limit(1)
    )
  );
  if (snapshot.empty) return null;
  return { ...snapshot.docs[0].data(), snapshotDate: snapshot.docs[0].id };
};

// The same shape as the getInsightsHistory Function, oldest period first.
export const getMyUserInsightsHistory = async (uid, snapshotDate, period) => {
  const end = moment(snapshotDate, "M-D-YYYY", true);
  if (!end.isValid()) return [];
  const days = parseInt(period, 10);
  return Promise.all([5, 4, 3, 2, 1].map(async (offset) => {
    const date = end.clone().subtract(days * offset, "days").format("M-D-YYYY");
    const snapshot = await getDoc(doc(FirestoreDB, `users/${uid}/userCalculations/${date}`));
    if (!snapshot.exists()) return null;
    return snapshot.data().meetingCost?.[period] ?? null;
  }));
};

export const getOrgTimeHistory = async (uid, snapshotDate, period) => {
  const end = moment(snapshotDate, "M-D-YYYY", true);
  if (!end.isValid()) return [];
  const days = parseInt(period, 10);
  return Promise.all([5, 4, 3, 2, 1].map(async (offset) => {
    const date = end.clone().subtract(days * offset, "days").format("M-D-YYYY");
    const snapshot = await getDoc(doc(FirestoreDB, `users/${uid}/orgCalculations/${date}`));
    if (!snapshot.exists()) return null;
    const { timeSplit, meetingCost } = snapshot.data();
    // Each perspective holds its time split plus the cost for the cost trend.
    return Object.fromEntries(
      ["IncludingSelf", "ExcludingSelf"].map((self) => [
        self,
        {
          ...timeSplit?.[period]?.[self],
          totalMeetingsCost: meetingCost?.[period]?.[self]?.totalMeetingsCost,
        },
      ]),
    );
  }));
};
